// client/src/modules/quoting/routes.tsx
import React from 'react';
import type { RouteObject } from 'react-router-dom';
import { guardPage, GuardedPage } from '../pageGuard';
import { QUOTING_PAGES } from '../pageAccess';
import { lazyWithRetry } from '../../lib/lazyWithRetry';
import { WorksheetPreviewGate } from '../../components/Quoting/v2/WorksheetPreviewGate';

// Lazy load all Quoting pages for code splitting
// Exported so aboutRemoved.test.tsx can assert the catch-all and index routes
// render THIS specific component, not merely "the same component as each other".
export const QuotingDashboard = lazyWithRetry(() => import('../../pages/Quoting/QuotingDashboard'));
const QuoteList = lazyWithRetry(() => import('../../pages/Quotes/QuoteList'));
const QuoteCreate = lazyWithRetry(() => import('../../pages/Quotes/QuoteCreate'));
const QuoteDetail = lazyWithRetry(() => import('../../pages/Quotes/QuoteDetail'));
const QuoteOutputPage = lazyWithRetry(() => import('../../pages/Quotes/QuoteOutput'));
const FollowUpQueue = lazyWithRetry(() => import('../../pages/Quotes/FollowUpQueue'));
const TemplateList = lazyWithRetry(() => import('../../pages/Quotes/TemplateList'));
const CustomerList = lazyWithRetry(() => import('../../pages/Customers/CustomerList'));
const CustomerDetail = lazyWithRetry(() => import('../../pages/Customers/CustomerDetail'));
const EquipmentList = lazyWithRetry(() => import('../../pages/Equipment/EquipmentList'));
const EquipmentDetail = lazyWithRetry(() => import('../../pages/Equipment/EquipmentDetail'));
const PriceBook = lazyWithRetry(() => import('../../pages/PriceBook'));
const RateSheetSettings = lazyWithRetry(() => import('../../pages/Admin/RateSheetSettings'));
const QuoteAnalytics = lazyWithRetry(() => import('../../pages/Quotes/QuoteAnalytics'));
// The quote worksheet's screens (#6274 split): each behind WorksheetPreviewGate. The worksheet opens with the organization's
// live switch, or for a manager with worksheetForManagers on; the price-catalog screens (screen="catalog") also for a manager
// with previewForManagers on, the price-catalog preview, which never opens the worksheet (DR-O87-2, #8450).
const PriceCatalog = lazyWithRetry(() => import('../../pages/Quoting/PriceCatalog'));
const PricingSettings = lazyWithRetry(() => import('../../pages/Quoting/PricingSettings'));
const CustomerQuoteSettings = lazyWithRetry(() => import('../../pages/Quoting/CustomerQuoteSettings'));
const WorksheetEntry = lazyWithRetry(() => import('../../pages/Quotes/v2/QuoteCreateEntry'));
// The Create/View Quote switches (Q9 cutover) load lazily too: they reach PageLayout and the worksheet hooks, which lead back
// to this file, so an eager import closes a load-time cycle (check-import-graph).
const QuoteCreateSwitch = lazyWithRetry(() => import('../../components/Quoting/v2/QuoteRouteSwitch').then((m) => ({ default: m.QuoteCreateSwitch })));
const QuoteOpenSwitch = lazyWithRetry(() => import('../../components/Quoting/v2/QuoteRouteSwitch').then((m) => ({ default: m.QuoteOpenSwitch })));

/**
 * Quoting module routes - relative to /quoting prefix
 */
export const routes: RouteObject[] = [
  {
    index: true,
    ...guardPage(QUOTING_PAGES.quoting, <QuotingDashboard />),
  },
  {
    path: 'quotes',
    ...guardPage(QUOTING_PAGES.quoting, <QuoteList />),
  },
  {
    // Every Create Quote button lands here. With the worksheet on (Q9 cutover) a new quote, or a saved worksheet
    // draft, forwards to the worksheet; an older draft and an old quote template stay on the old screen.
    path: 'quotes/create',
    ...guardPage(QUOTING_PAGES.quoting, <QuoteCreateSwitch legacy={<QuoteCreate />} />),
  },
  {
    // Alias for /quotes/create — used by CRM "Create Quote" buttons
    path: 'quotes/new',
    ...guardPage(QUOTING_PAGES.quoting, <QuoteCreateSwitch legacy={<QuoteCreate />} />),
  },
  {
    // Every View Quote lands here. With the worksheet on, a worksheet quote opens in the worksheet; older quotes keep
    // this page.
    path: 'quotes/:quoteId',
    ...guardPage(QUOTING_PAGES.quoting, <QuoteOpenSwitch legacy={<QuoteDetail />} />),
  },
  {
    path: 'quotes/:quoteId/output',
    ...guardPage(QUOTING_PAGES.quoting, <QuoteOutputPage />),
  },
  {
    path: 'analytics',
    ...guardPage(QUOTING_PAGES.quoting, <QuoteAnalytics />),
  },
  {
    path: 'follow-up-queue',
    ...guardPage(QUOTING_PAGES.quoting, <FollowUpQueue />),
  },
  {
    path: 'templates',
    ...guardPage(QUOTING_PAGES.quoting, <TemplateList />),
  },
  {
    path: 'customers',
    ...guardPage(QUOTING_PAGES.customers, <CustomerList />),
  },
  {
    // P-B1: a real customer detail page, replacing modal-only viewing.
    // B2 (work + form history) and B3 (file attach) build on this shell.
    path: 'customers/:customerId',
    ...guardPage(QUOTING_PAGES.customerDetail, <CustomerDetail />),
  },
  {
    path: 'equipment',
    ...guardPage(QUOTING_PAGES.equipment, <EquipmentList />),
  },
  {
    path: 'equipment/:equipmentId',
    ...guardPage(QUOTING_PAGES.equipment, <EquipmentDetail />),
  },
  {
    path: 'price-book',
    ...guardPage(QUOTING_PAGES.quoting, <PriceBook />),
  },
  {
    // In the bar while the worksheet is on for the viewer (nav.ts worksheetNavItems).
    path: 'price-catalog',
    ...guardPage(QUOTING_PAGES.quoting, <WorksheetPreviewGate screen="catalog"><PriceCatalog /></WorksheetPreviewGate>),
  },
  {
    // The quote price sheet (labor, materials, services, install lists): the price-catalog screens' gate.
    path: 'pricing',
    ...guardPage(QUOTING_PAGES.quoting, <WorksheetPreviewGate screen="catalog"><PricingSettings /></WorksheetPreviewGate>),
  },
  {
    // Templates (the customer quote wording): the price-catalog screens' gate.
    path: 'customer-quote',
    ...guardPage(QUOTING_PAGES.quoting, <WorksheetPreviewGate screen="catalog"><CustomerQuoteSettings /></WorksheetPreviewGate>),
  },
  {
    // The one-page quote worksheet: the worksheet's gate. Create Quote forwards here when it is on (QuoteCreateSwitch). The gate decides
    // which SCREEN is shown; it is not a data boundary. The quote itself is a plain document read that firestore.rules allows
    // to anyone who can already read quotes, whatever the preview switch says (PR #8437 security S3). What the preview holds
    // back for real is enforced on the server: the PDF callable and the revision history check the switch themselves.
    path: 'worksheet',
    ...guardPage(QUOTING_PAGES.quoting, <WorksheetPreviewGate><WorksheetEntry /></WorksheetPreviewGate>),
  },
  {
    path: 'rate-sheet',
    ...guardPage(QUOTING_PAGES.quoting, <RateSheetSettings />),
  },
  {
    // Written out (not `...guardPage(...)`) so check-module-catchall-absolute can SEE the element: a
    // spread is opaque to it. Same shape guardPage builds: GuardedPage + the matching handle.
    path: '*',
    element: <GuardedPage access={QUOTING_PAGES.quoting}><QuotingDashboard /></GuardedPage>,
    handle: { anyOf: QUOTING_PAGES.quoting.anyOf, allowExternal: false },
  },
];
